import { Component } from '@angular/core';
import { MatCard } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import moment from 'moment';
import { NgxMaskService } from 'ngx-mask';
import { forkJoin } from 'rxjs';
import { FORMLY_FIELD_TYPES, Masks } from '@app/constants';
import { FeedbackService, ReportService, RoleService } from '@app/core/services';
import { UserClaims } from '@app/enums';
import { CellTransform, FilterConfig, TableConfig } from '@app/interfaces';
import { BaseListComponent, TableComponent, TitleButton, TitleButtonsComponent, TitleComponent } from '@app/shared/components';
import { FiltersComponent } from '@app/shared/forms/components/filters/filters.component';
import { SiteService } from '@app/shared/services/site.service';
import { UserService } from '@app/shared/services/user.service';
import { mapArrayToSelectOptions } from '@app/utils';
import { ReadOnly } from '@app/utils/claims';
import { AddUserFormComponent } from '../components/add-user-form.component';
import { AddUserModel } from '../models/add-user.model';
import { UserListItem } from '../models/user-list-item.model';

@Component({
	selector: 'sl-user-list',
	standalone: true,
	imports: [TableComponent, TitleComponent, FiltersComponent, MatCard, TitleButtonsComponent],
	providers: [UserService, RoleService, SiteService],
	templateUrl: './list.component.html',
	styleUrl: './list.component.less',
})
export class UserListComponent extends BaseListComponent<UserListItem, UserService> {
	@ReadOnly()
	isReadOnly = true;

	entityName: string = 'User';
	listingPagePath: string = '/lsb/systemSettings/users';
	listArrayPropertyName: string = 'users';

	titleButtons: TitleButton[] = [
		{
			label: 'Add User',
			icon: 'add',
			onClick: (): void => this.save(),
			disabled: (): boolean => this.isReadOnly,
		},
	];
	filterOptions: FormlyFormOptions = {
		formState: {
			selects: {
				roles: [],
			},
		},
	};
	filterConfig: FilterConfig<any>[] = [];
	filterFields: FormlyFieldConfig[] = [
		{
			key: 'RoleName',
			className: 'col-xs-6',
			type: FORMLY_FIELD_TYPES.select,
			props: {
				label: 'Role',
				options: [],
				placeholder: 'Select Role',
			},
			expressions: {
				'props.options': 'formState.selects.roles',
			},
		},
		{
			key: 'Status',
			className: 'col-xs-6',
			type: FORMLY_FIELD_TYPES.select,
			props: {
				label: 'Status',
				options: [
					{
						label: 'Active',
						value: 'Active',
					},
					{
						label: 'Inactive',
						value: 'Inactive',
					},
				],
			},
		},
	];
	filters = { search: '' };

	entity: AddUserModel = {
		Id: null,
		Email: null,
		UserName: null,
		FirstName: null,
		LastName: null,
		FullName: null,
		RoleId: null,
		PhoneNumber: null,
		SiteId: null,
		DuplicateFromUserId: null,
		Active: null,
		RoleDescription: null,
	};

	roles: any[] = [];
	sites: any[] = [];

	config: TableConfig<UserListItem> = {
		defaultSortDirection: 'asc',
		showRowSelect: false,
		showRowActions: true,
		selectedId: null,
		defaultSortField: 'UserName',
		actions: [
			{
				label: 'Details',
				onClick: (row: UserListItem): void => {
					this.router.navigate(['lsb/systemSettings/users', row.Id]);
				},
				disabled: (): boolean => this.isReadOnly,
			},
			{
				label: 'Duplicate',
				hidden: (row: UserListItem): boolean => row.RoleName === UserClaims.globalAdmin,
				onClick: (row: UserListItem): void => this.duplicate(row),
				disabled: (): boolean => this.isReadOnly,
			},
		],
		bulkActions: [],
		columns: [
			{
				header: 'User Name',
				key: 'UserName',
			},
			{
				header: 'Full Name',
				key: 'FullName',
			},
			{
				header: 'Status',
				key: 'Active',
				transform: ({ row }: CellTransform<UserListItem>): string => (row.Active ? 'Active' : 'Inactive'),
			},
			{
				header: 'Role',
				key: 'RoleName',
			},
			{
				header: 'Email Address',
				key: 'Email',
			},
			{
				header: 'Phone Number',
				key: 'PhoneNumber',
				transform: ({ row }: CellTransform<UserListItem>): string => this.maskService.applyMask(row.PhoneNumber, Masks.Phone) || 'N/A',
			},
			{
				header: 'Last Login',
				key: 'LastLogin',
				// eslint-disable-next-line import/no-named-as-default-member
				transform: ({ row }: CellTransform<UserListItem>): string => (row.LastLogin ? moment.utc(row.LastLogin).local().calendar() : 'Never'),
			},
		],
		onRowClick: (row: any) => (this.config.selectedId = row.Id),
	};

	constructor(
		protected readonly listService: UserService,
		protected readonly dialog: MatDialog,
		protected readonly feedbackService: FeedbackService,
		protected readonly route: ActivatedRoute,
		protected readonly router: Router,
		protected readonly reportService: ReportService,
		private readonly roleService: RoleService,
		private readonly siteService: SiteService,
		private readonly maskService: NgxMaskService
	) {
		super(dialog, feedbackService, route, router, reportService);
	}

	filtersUpdated(filters: any) {
		this.applyFilter(filters);
	}

	protected afterOnInit(): void {
		this.roleService.list().subscribe((data) => {
			this.filterOptions.formState.selects.roles = mapArrayToSelectOptions(data.roles, {
				value: 'Name',
				label: 'Name',
				all: true,
			});
		});
	}

	duplicate(row: UserListItem) {
		this.listService.getUser(row.Id).subscribe((data) => {
			this.entity.RoleId = data.Role.Id;
			this.entity.RoleDescription = data.Role.Description;
			this.entity.DuplicateFromUserId = row.Id;

			this.save();
		});
	}

	save(): void {
		this.loadDependencies().then(() => {
			this.dialog
				.open(AddUserFormComponent, {
					minWidth: '70vw',
					data: {
						entity: this.entity,
						roles: this.roles,
						sites: this.sites,
						title: this.entity?.DuplicateFromUserId != null ? 'Duplicate User' : 'Add User',
						users: this.data,
					},
				})
				.afterClosed()
				.subscribe((entity) => {
					if (!entity) {
						return;
					}

					this.feedbackService.success('created', 'User');

					this.refresh();
				});
		});
	}

	private loadDependencies(): Promise<void> {
		return new Promise((resolve) => {
			forkJoin([this.roleService.list(), this.siteService.getList()]).subscribe(([roles, sites]) => {
				this.roles = roles.roles;
				this.sites = sites.sites;
				resolve();
			});
		});
	}
}
